/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomNavigate } from '../../../../shared/hooks/use-conversation-navigate';
import { QSButton, QSTypography } from '../../../../shared/components/atoms';
import LockUnauthorizedIcon from '../../../../shared/icons/icons-revamp/lock_unauthorized';
import { ROUTES } from '../../../../shared/constants/routes';
import OrgOwnersList from './org-owners-list';

interface ProtectedPlaceholderProps {
  onClick?: () => void;
  heading?: string;
  description?: string;
  buttonText?: string;
  showButton?: boolean;
  showOrgOwners?: boolean;
  featureName?: string;
}

const FEATURE_NAME_FALLBACK = 'this page';

const isIdLikeSegment = (segment: string): boolean => {
  if (/^v\d+$/i.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  if (/^[0-9a-f]{12,}$/i.test(segment)) return true;
  if (/^\+?\d{6,}$/.test(segment)) return true;
  return false;
};

const toTitleCase = (segment: string): string =>
  segment
    .split('-')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

const deriveFeatureName = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (!isIdLikeSegment(segments[i])) {
      return toTitleCase(segments[i]);
    }
  }
  return FEATURE_NAME_FALLBACK;
};

const ProtectedPlaceholder: FC<ProtectedPlaceholderProps> = ({
  heading,
  description = 'Please contact your org owner to share access',
  onClick,
  buttonText = 'Go to Home',
  showButton = false,
  showOrgOwners = true,
  featureName,
}) => {
  const navigate = useCustomNavigate();
  const location = useLocation();

  const resolvedFeatureName = useMemo(
    () => featureName ?? deriveFeatureName(location.pathname),
    [featureName, location.pathname]
  );
  const resolvedHeading = heading ?? `You don't have access to ${resolvedFeatureName}`;

  return (
    <div
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: safe center;
        flex-direction: column;
        row-gap: 24px;
        padding: 48px 24px;
        overflow-y: auto;
      `}
    >
      <div
        css={css`
          width: 56px;
          height: 56px;
          flex-shrink: 0;
          border-radius: 50%;
          background-color: #ef53501f;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <LockUnauthorizedIcon size={20} />
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          row-gap: 8px;
        `}
      >
        <QSTypography
          css={css`
            font-size: 16px;
            font-weight: 600;
            line-height: 23.4px;
            letter-spacing: -0.27px;
            text-align: center;
            word-wrap: break-word;
            word-break: break-word;
            color: #2d2d2d;
          `}
        >
          {resolvedHeading}
        </QSTypography>
        <QSTypography
          css={css`
            font-size: 14px;
            font-weight: 400;
            line-height: 21px;
            letter-spacing: -0.04px;
            text-align: center;
            color: #7a7a7a;
            word-wrap: break-word;
            word-break: break-word;
          `}
        >
          {description}
        </QSTypography>
      </div>
      {showOrgOwners && <OrgOwnersList featureName={resolvedFeatureName} />}
      {showButton && (
        <QSButton
          css={css`
            color: white;
            padding: 10px 20px;
            font-size: 14px;
            margin-top: 8px;
            transition: all 0.2s ease-in-out;

            &:hover {
              scale: 1.05;
              background-color: #3f906d;
            }
          `}
          onClick={() => {
            if (onClick) {
              onClick();
            } else {
              navigate({ pathname: ROUTES.DASHBOARD.replace('/*', '/') });
            }
          }}
        >
          {buttonText}
        </QSButton>
      )}
    </div>
  );
};

export default ProtectedPlaceholder;
